// PreviewTester/components/ActionFieldsBuilder.js
import React, { useState, useEffect, useRef } from 'react';
import { MappingExplainer } from '../PreviewTesterHome/PreviewTester/components/SkillFieldsMapper';

// ⭐ NEW: shared explainer banner — same component used by SkillFieldsMapper.
//    It documents where `item` and `response_data` come from, which is the
//    single biggest source of user confusion on this screen ("why am I
//    seeing fields that aren't visible on the screen?").


const FIELD_TYPES = ['path_param', 'query_param', 'payload'];

/**
 * Validates a mapping value against the template grammar:
 *
 *   value        := (text | fieldRef)*
 *   fieldRef     := "{{" WS* path WS* "}}"
 *   path         := identifier ("." identifier)*
 *   identifier   := [A-Za-z_][A-Za-z0-9_]*
 *
 * Accepts:
 *   ""                       (empty → treated as unmapped)
 *   "hello"
 *   "123"
 *   "custom_id_42"
 *   "{{ item.id }}"
 *   "prefix__{{ item.id }}"
 *   "{{ a.b.c }}-{{ d.e }}"
 *
 * Rejects:
 *   "{{ item.id"             (unbalanced open)
 *   "item.id }}"             (unbalanced close)
 *   "{{}}"                   (empty mustache)
 *   "{{ }}"                  (whitespace-only mustache)
 *   "{{ 123.field }}"        (identifier starts with digit)
 *   "{{ . }}"                (bare dot / no identifier)
 *   "{{ a {{ b }}"           (nested mustache)
 *   "{{ a.b }"               (unclosed mustache before a later "}}")
 */
const isValidTemplate = (value) => {
  // Empty is a valid "no mapping yet" state (parent decides if that's OK).
  if (value === null || value === undefined) return true;
  const str = String(value);
  if (str.trim() === '') return true;

  // Walk through the string, extracting every mustache pair and
  // validating its contents. Anything between mustaches is treated as
  // free text and left alone.
  let i = 0;
  const len = str.length;

  while (i < len) {
    const openIdx = str.indexOf('{{', i);
    if (openIdx === -1) {
      // No more mustaches — the remaining text is free-form, which is fine.
      // But if we're inside a mustache (handled below), we would have exited
      // already.
      break;
    }

    const closeIdx = str.indexOf('}}', openIdx + 2);
    if (closeIdx === -1) {
      // Opened a mustache and never closed it.
      return false;
    }

    // The inner content between {{ and }}.
    const inner = str.slice(openIdx + 2, closeIdx);

    // Nested "{{" inside the mustache — not allowed.
    if (inner.includes('{{')) return false;

    // Must have a non-empty path.
    const trimmedInner = inner.trim();
    if (trimmedInner === '') return false;

    // Path must be identifier(.identifier)*
    // identifier: [A-Za-z_][A-Za-z0-9_]*
    const pathRegex = /^[A-Za-z_][A-Za-z0-9_]*(\.[A-Za-z_][A-Za-z0-9_]*)*$/;
    if (!pathRegex.test(trimmedInner)) return false;

    // Advance past this mustache.
    i = closeIdx + 2;
  }

  // Now scan for any stray "}}" that isn't preceded by a "{{".
  // We'll do this by walking again, tracking brace balance.
  let depth = 0;
  for (let j = 0; j < str.length; j++) {
    if (str[j] === '{' && str[j + 1] === '{') {
      depth++;
      j++; // skip second '{'
    } else if (str[j] === '}' && str[j + 1] === '}') {
      depth--;
      if (depth < 0) return false; // stray closing
      j++;
    }
  }
  if (depth !== 0) return false;

  return true;
};

const ActionFieldsBuilder = ({
  apiConfig,
  availableFields = [],
  onMappingChange,
  readOnly = false,
  initialMappings = {},
}) => {
  const [mappings, setMappings] = useState({});
  const [showTemplateEditor, setShowTemplateEditor] = useState({});
  const inputRefs = useRef({});

  // Initialize mappings from apiConfig
  useEffect(() => {
    if (!apiConfig) {
      setMappings({});
      return;
    }

    const apiCfg = apiConfig.api_config || {};
    const newMappings = {
      path_param: {},
      query_param: {},
      payload: {},
    };

    // Extract path parameters from URL
    const url = apiCfg.url || '';
    const pathParamMatches = url.match(/\{\{\s*(\w+)\s*\}\}/g);
    if (pathParamMatches) {
      pathParamMatches.forEach((match) => {
        const name = match.replace(/[\{\}\s]/g, '');
        const initialValue = initialMappings?.path_param?.[name] || '';
        newMappings.path_param[name] = initialValue;
      });
    }

    // Extract query parameters
    const queryParams = apiCfg.query_params || {};
    Object.keys(queryParams).forEach((key) => {
      const value = queryParams[key];
      if (typeof value === 'string' && value.includes('{{')) {
        const varMatch = value.match(/\{\{\s*(\w+)\s*\}\}/);
        if (varMatch) {
          const initialValue = initialMappings?.query_param?.[key] || '';
          newMappings.query_param[key] = initialValue;
        }
      } else {
        const initialValue = initialMappings?.query_param?.[key] || '';
        newMappings.query_param[key] = initialValue;
      }
    });

    // Extract payload fields
    const payloadTemplate = apiCfg.payload_template || '{}';
    const payloadMatches = payloadTemplate.match(/\{\{\s*(\w+)\s*\}\}/g);
    if (payloadMatches) {
      payloadMatches.forEach((match) => {
        const name = match.replace(/[\{\}\s]/g, '');
        const initialValue = initialMappings?.payload?.[name] || '';
        newMappings.payload[name] = initialValue;
      });
    }

    setMappings(newMappings);
  }, [apiConfig, initialMappings]);

  const handleMappingUpdate = (paramType, apiField, templateValue) => {
    const updatedMappings = {
      ...mappings,
      [paramType]: {
        ...mappings[paramType],
        [apiField]: templateValue,
      },
    };
    setMappings(updatedMappings);

    if (onMappingChange) {
      // ⭐ Pass a second argument so the parent (Controls) can decide
      //    whether to disable "Add Trigger".
      const allValid = allMappingsAreValid(updatedMappings);
      onMappingChange(updatedMappings, allValid);
    }
  };

  // ============================================================
  // Validity helpers
  // ============================================================

  /**
   * Given the whole `mappings` object, decide if every single entry
   * is either empty or a valid template string.
   *
   * Note: an *empty* entry is treated as valid by this helper — but
   * Controls.js separately checks that the required count is met
   * (unmappedCount === 0). Both conditions must hold for the trigger
   * button to be enabled.
   */
  const allMappingsAreValid = (mappingObj) => {
    if (!mappingObj) return true;
    for (const typeMappings of Object.values(mappingObj)) {
      for (const v of Object.values(typeMappings)) {
        if (!isValidTemplate(v)) return false;
      }
    }
    return true;
  };

  const currentAllValid = allMappingsAreValid(mappings);

  const getParamTypeLabel = (type) => {
    const labels = {
      path_param: '🔗 Path Parameter',
      query_param: '❓ Query Parameter',
      payload: '📦 Payload Field',
    };
    return labels[type] || type;
  };

  const getParamTypeColor = (type) => {
    const colors = {
      path_param: '#2b6cb0',
      query_param: '#38a169',
      payload: '#9f7aea',
    };
    return colors[type] || '#718096';
  };

  const getParamTypeBg = (type) => {
    const colors = {
      path_param: '#ebf8ff',
      query_param: '#f0fff4',
      payload: '#faf5ff',
    };
    return colors[type] || '#f7fafc';
  };

  // Get available fields with display labels
  const getAvailableFieldsWithLabels = () => {
    if (!availableFields || availableFields.length === 0) {
      return [];
    }
    return availableFields.map((fieldItem) => ({
      value: fieldItem,
      label: fieldItem.replace(/\./g, ' → '),
    }));
  };

  const availableFieldsWithLabels = getAvailableFieldsWithLabels();

  // Helper to detect if a value is a simple field reference or a template
  const isSimpleFieldReference = (value) => {
    if (!value) return false;
    const simpleFieldPattern = /^[a-zA-Z_][a-zA-Z0-9_.]*$/;
    return simpleFieldPattern.test(value) && !value.includes('{{');
  };

  const insertFieldIntoTemplate = (
    fieldItem,
    currentTemplate,
    paramType,
    apiField,
  ) => {
    const key = `${paramType}_${apiField}`;
    const inputRef = inputRefs.current[key];
    const cursorPos = inputRef?.selectionStart || currentTemplate.length;
    const before = currentTemplate.substring(0, cursorPos);
    const after = currentTemplate.substring(cursorPos);
    const newTemplate = before + `{{ ${fieldItem} }}` + after;

    handleMappingUpdate(paramType, apiField, newTemplate);

    // Close the dropdown
    setShowTemplateEditor((prev) => ({
      ...prev,
      [key]: false,
    }));

    // Focus back on input
    setTimeout(() => {
      if (inputRef) {
        inputRef.focus();
        const newPos = cursorPos + `{{ ${fieldItem} }}`.length;
        inputRef.setSelectionRange(newPos, newPos);
      }
    }, 10);
  };

  const toggleTemplateEditor = (paramType, apiField) => {
    const key = `${paramType}_${apiField}`;
    setShowTemplateEditor((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const renderTemplateEditor = (paramType, apiField, currentValue) => {
    const key = `${paramType}_${apiField}`;
    const isOpen = showTemplateEditor[key] || false;
    const isSimple = isSimpleFieldReference(currentValue);
    const isTemplate = currentValue && currentValue.includes('{{');

    // ⭐ Validation for THIS field
    const isValid = isValidTemplate(currentValue);
    const hasValue = currentValue && currentValue.trim() !== '';
    const showError = hasValue && !isValid;

    // Border colour resolution:
    //   • invalid           → red
    //   • valid + non-empty → green
    //   • empty             → grey (default)
    const borderColor = showError
      ? '#e53e3e'
      : hasValue && isValid
        ? '#38a169'
        : '#e2e8f0';

    const backgroundColor = showError
      ? '#fff5f5'
      : hasValue && isValid
        ? '#f0fff4'
        : 'white';

    return (
      <div style={{ flex: 1, position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <input
            ref={(el) => {
              inputRefs.current[key] = el;
            }}
            type="text"
            value={currentValue || ''}
            onChange={(e) => {
              handleMappingUpdate(paramType, apiField, e.target.value);
            }}
            placeholder="e.g., {{ item.id }} or custom_id__{{ item.id }}"
            style={{
              flex: 1,
              padding: '4px 8px',
              border: `2px solid ${borderColor}`,
              borderRadius: '4px',
              fontSize: '12px',
              fontFamily: 'monospace',
              backgroundColor,
              outline: 'none',
              transition: 'border-color 0.2s, background-color 0.2s',
            }}
            onFocus={(e) => {
              // Keep the semantic colour on focus, don't override it.
              // Only bump the outline for accessibility.
              e.target.style.boxShadow = '0 0 0 2px rgba(66, 153, 225, 0.35)';
            }}
            onBlur={(e) => {
              e.target.style.boxShadow = 'none';
              // Don't close dropdown immediately on blur
              setTimeout(() => {
                setShowTemplateEditor((prev) => ({
                  ...prev,
                  [key]: false,
                }));
              }, 200);
            }}
          />
          <button
            onClick={() => toggleTemplateEditor(paramType, apiField)}
            style={{
              padding: '2px 8px',
              backgroundColor: isOpen ? '#38a169' : '#4299e1',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '11px',
              whiteSpace: 'nowrap',
            }}
            title="Insert field into template"
          >
            {isOpen ? '✕ Close' : '+ Field'}
          </button>
        </div>

        {/* Inline error message */}
        {showError && (
          <div
            style={{
              marginTop: '2px',
              fontSize: '10px',
              color: '#c53030',
              display: 'flex',
              gap: '6px',
              alignItems: 'center',
            }}
          >
            <span>⚠️</span>
            <span>
              Invalid template. Use <code>{'{{ field.path }}'}</code> syntax
              with balanced braces and valid identifiers.
            </span>
          </div>
        )}

        {/* Field quick-insert dropdown */}
        {isOpen && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              backgroundColor: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '4px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              zIndex: 10,
              maxHeight: '200px',
              overflowY: 'auto',
              marginTop: '4px',
            }}
          >
            <div
              style={{
                padding: '6px 10px',
                fontSize: '10px',
                color: '#718096',
                borderBottom: '1px solid #e2e8f0',
                backgroundColor: '#f7fafc',
                position: 'sticky',
                top: 0,
                zIndex: 1,
              }}
            >
              Click a field to insert it at cursor position
            </div>
            {availableFieldsWithLabels.length === 0 ? (
              <div
                style={{
                  padding: '12px',
                  textAlign: 'center',
                  color: '#a0aec0',
                  fontSize: '12px',
                }}
              >
                No fields available. Please render a preview first.
              </div>
            ) : (
              availableFieldsWithLabels.map((fieldItem) => (
                <div
                  key={fieldItem.value}
                  onClick={() => {
                    insertFieldIntoTemplate(
                      fieldItem.value,
                      currentValue,
                      paramType,
                      apiField,
                    );
                  }}
                  style={{
                    padding: '6px 12px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    borderBottom: '1px solid #f7fafc',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = '#f0f4ff')
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = 'white')
                  }
                >
                  <span>{fieldItem.label}</span>
                  <span
                    style={{
                      fontSize: '9px',
                      color: '#4299e1',
                      backgroundColor: '#ebf8ff',
                      padding: '1px 6px',
                      borderRadius: '8px',
                    }}
                  >
                    Insert
                  </span>
                </div>
              ))
            )}
          </div>
        )}

        {/* Template preview / validation (only when valid) */}
        {currentValue && currentValue.includes('{{') && isValid && (
          <div
            style={{
              marginTop: '2px',
              fontSize: '9px',
              color: '#38a169',
              fontStyle: 'italic',
              display: 'flex',
              gap: '12px',
              flexWrap: 'wrap',
            }}
          >
            <span>📝 Template</span>
            <span>
              Fields:{' '}
              {(currentValue.match(/\{\{\s*([^}]+)\s*\}\}/g) || [])
                .map((m) => m.replace(/[\{\}\s]/g, ''))
                .join(', ')}
            </span>
          </div>
        )}

        {currentValue &&
          !currentValue.includes('{{') &&
          !isSimple &&
          currentValue.trim() !== '' && (
            <div
              style={{
                marginTop: '2px',
                fontSize: '9px',
                color: '#ed8936',
                fontStyle: 'italic',
              }}
            >
              ⚠️ No field references found. Use "{'{'} {'{'} field {'}'} {'}'}"
              to reference response data.
            </div>
          )}
      </div>
    );
  };

  if (!apiConfig) {
    return (
      <div
        style={{
          padding: '16px',
          backgroundColor: '#f7fafc',
          borderRadius: '8px',
          border: '1px dashed #e2e8f0',
          textAlign: 'center',
          color: '#a0aec0',
          fontSize: '13px',
        }}
      >
        Select an API Config to see its required fields
      </div>
    );
  }

  const totalMappings = Object.values(mappings).reduce(
    (acc, typeMappings) => acc + Object.keys(typeMappings).length,
    0,
  );

  const completedMappings = Object.values(mappings).reduce(
    (acc, typeMappings) =>
      acc + Object.values(typeMappings).filter((v) => v && v.trim() !== '').length,
    0,
  );

  const invalidMappings = Object.values(mappings).reduce(
    (acc, typeMappings) =>
      acc + Object.values(typeMappings).filter((v) => !isValidTemplate(v)).length,
    0,
  );

  if (totalMappings === 0) {
    return (
      <div
        style={{
          padding: '16px',
          backgroundColor: '#f7fafc',
          borderRadius: '8px',
          border: '1px dashed #e2e8f0',
          textAlign: 'center',
          color: '#a0aec0',
          fontSize: '13px',
        }}
      >
        No required fields detected in this API config.
        <br />
        <span style={{ fontSize: '12px' }}>
          The API may not have any path, query, or payload parameters.
        </span>
      </div>
    );
  }

  if (availableFieldsWithLabels.length === 0) {
    return (
      <div
        style={{
          padding: '16px',
          backgroundColor: '#fefcbf',
          borderRadius: '8px',
          border: '1px solid #f6ad55',
          textAlign: 'center',
          color: '#744210',
          fontSize: '13px',
        }}
      >
        ⚠️ No response fields available for mapping.
        <br />
        <span style={{ fontSize: '12px' }}>
          Please render a preview first to see available fields from your sample
          data.
        </span>
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
        padding: '16px',
        marginTop: '12px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px',
        }}
      >
        <div>
          <span
            style={{
              fontWeight: '600',
              color: '#2d3748',
              fontSize: '13px',
            }}
          >
            🔄 Response-to-Request Field Mapping
          </span>
          <span
            style={{
              marginLeft: '12px',
              fontSize: '11px',
              color: '#718096',
            }}
          >
            ({completedMappings}/{totalMappings} mapped)
          </span>
          {invalidMappings > 0 && (
            <span
              style={{
                marginLeft: '8px',
                fontSize: '11px',
                color: '#c53030',
                backgroundColor: '#fff5f5',
                padding: '1px 8px',
                borderRadius: '8px',
                border: '1px solid #fed7d7',
              }}
            >
              ⚠️ {invalidMappings} invalid
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span
            style={{
              fontSize: '10px',
              color: '#718096',
              backgroundColor: '#edf2f7',
              padding: '2px 8px',
              borderRadius: '8px',
            }}
          >
            💡 Use {'{'} {'{'} field {'}'} {'}'} syntax
          </span>
          {readOnly && (
            <span
              style={{
                fontSize: '11px',
                color: '#718096',
                backgroundColor: '#edf2f7',
                padding: '2px 10px',
                borderRadius: '10px',
              }}
            >
              Read-only
            </span>
          )}
        </div>
      </div>

      {/* ⭐ NEW: Explainer banner — same component used by SkillFieldsMapper.
          Documents where `item` and `response_data` come from, so users
          understand why the dropdown contains fields that aren't visibly
          rendered on the screen. */}
      <MappingExplainer tone="default" availableFields={availableFields} />

      {/* Parameter Type Sections */}
      {FIELD_TYPES.map((paramType) => {
        const typeMappings = mappings[paramType] || {};
        const paramKeys = Object.keys(typeMappings);

        if (paramKeys.length === 0) return null;

        const color = getParamTypeColor(paramType);
        const bg = getParamTypeBg(paramType);
        const completed = Object.values(typeMappings).filter(
          (v) => v && v.trim() !== '',
        ).length;

        return (
          <div
            key={paramType}
            style={{
              marginBottom: '12px',
              padding: '12px',
              backgroundColor: bg,
              borderRadius: '6px',
              border: `1px solid ${color}33`,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px',
              }}
            >
              <span
                style={{
                  fontWeight: '600',
                  color: color,
                  fontSize: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                {getParamTypeLabel(paramType)}
              </span>
              <span
                style={{
                  fontSize: '11px',
                  color: '#718096',
                }}
              >
                {completed}/{paramKeys.length} mapped
              </span>
            </div>

            {paramKeys.map((apiField) => {
              const currentValue = typeMappings[apiField] || '';
              const isMapped = currentValue && currentValue.trim() !== '';
              const isTemplate = currentValue && currentValue.includes('{{');
              const isSimple = isSimpleFieldReference(currentValue) && !isTemplate;
              const isValid = isValidTemplate(currentValue);
              const showError = isMapped && !isValid;

              return (
                <div
                  key={apiField}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '6px 8px',
                    marginBottom: '4px',
                    backgroundColor: showError
                      ? '#fff5f5'
                      : isMapped
                        ? '#f0fff4'
                        : 'white',
                    borderRadius: '4px',
                    border: `1px solid ${showError
                        ? '#fed7d7'
                        : isMapped
                          ? '#c6f6d5'
                          : '#e2e8f0'
                      }`,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      width: '100%',
                    }}
                  >
                    <span
                      style={{
                        fontWeight: '500',
                        color: '#2d3748',
                        fontSize: '12px',
                        fontFamily: 'monospace',
                        minWidth: '100px',
                      }}
                    >
                      {apiField}
                    </span>
                    <span
                      style={{
                        color: showError
                          ? '#e53e3e'
                          : isMapped
                            ? '#38a169'
                            : '#a0aec0',
                        fontSize: '12px',
                      }}
                    >
                      {isMapped ? '→' : '←'}
                    </span>
                    {readOnly ? (
                      <span
                        style={{
                          color: isMapped ? '#2b6cb0' : '#a0aec0',
                          fontSize: '12px',
                          fontFamily: 'monospace',
                          fontStyle: isMapped ? 'normal' : 'italic',
                          flex: 1,
                        }}
                      >
                        {currentValue || 'Not mapped'}
                      </span>
                    ) : (
                      renderTemplateEditor(paramType, apiField, currentValue)
                    )}
                    {currentValue && !readOnly && (
                      <button
                        onClick={() => {
                          handleMappingUpdate(paramType, apiField, '');
                        }}
                        style={{
                          backgroundColor: 'transparent',
                          border: 'none',
                          color: '#fc8181',
                          cursor: 'pointer',
                          fontSize: '14px',
                          padding: '0 4px',
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  {/* Type badge */}
                  <div
                    style={{
                      display: 'flex',
                      gap: '6px',
                      marginTop: '2px',
                      paddingLeft: '120px',
                    }}
                  >
                    {showError && (
                      <span
                        style={{
                          fontSize: '9px',
                          color: '#c53030',
                          backgroundColor: '#fff5f5',
                          padding: '0 6px',
                          borderRadius: '8px',
                          border: '1px solid #fed7d7',
                        }}
                      >
                        ⚠️ Invalid
                      </span>
                    )}
                    {!showError && isTemplate && (
                      <span
                        style={{
                          fontSize: '9px',
                          color: '#38a169',
                          backgroundColor: '#f0fff4',
                          padding: '0 6px',
                          borderRadius: '8px',
                        }}
                      >
                        📝 Template
                      </span>
                    )}
                    {!showError && isSimple && (
                      <span
                        style={{
                          fontSize: '9px',
                          color: '#2b6cb0',
                          backgroundColor: '#ebf8ff',
                          padding: '0 6px',
                          borderRadius: '8px',
                        }}
                      >
                        🔗 Field Reference
                      </span>
                    )}
                    {!showError &&
                      currentValue &&
                      !isTemplate &&
                      !isSimple &&
                      currentValue.trim() !== '' && (
                        <span
                          style={{
                            fontSize: '9px',
                            color: '#ed8936',
                            backgroundColor: '#fffaf0',
                            padding: '0 6px',
                            borderRadius: '8px',
                          }}
                        >
                          📝 Custom Value
                        </span>
                      )}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}

      {/* Summary */}
      {totalMappings > 0 && (
        <div
          style={{
            marginTop: '12px',
            padding: '8px 12px',
            backgroundColor: '#f7fafc',
            borderRadius: '4px',
            fontSize: '11px',
            color: '#4a5568',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '8px',
          }}
        >
          <span>
            <strong>Summary:</strong> {completedMappings} of {totalMappings}{' '}
            fields mapped
          </span>
          <div style={{ display: 'flex', gap: '12px', fontSize: '10px' }}>
            <span>
              📝 Templates:{' '}
              {Object.values(mappings).reduce(
                (acc, tm) =>
                  acc +
                  Object.values(tm).filter((v) => v && v.includes('{{')).length,
                0,
              )}
            </span>
            <span>
              🔗 Field refs:{' '}
              {Object.values(mappings).reduce(
                (acc, tm) =>
                  acc +
                  Object.values(tm).filter((v) => v && isSimpleFieldReference(v))
                    .length,
                0,
              )}
            </span>
          </div>
          {completedMappings === totalMappings && totalMappings > 0 && currentAllValid && (
            <span
              style={{
                color: '#38a169',
                fontWeight: '600',
              }}
            >
              ✅ All fields mapped
            </span>
          )}
          {invalidMappings > 0 && (
            <span
              style={{
                color: '#c53030',
                fontWeight: '600',
              }}
            >
              ⚠️ Fix {invalidMappings} invalid field
              {invalidMappings > 1 ? 's' : ''} before saving
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default ActionFieldsBuilder;